import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { MaterialReactTable } from "material-react-table";
import {
  Box,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  MenuItem,
  Button,
  Paper,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import GetAppIcon from "@mui/icons-material/GetApp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PrintIcon from "@mui/icons-material/Print";
import { URL } from "../config";

const InstallationReport = () => {
  const [dataSource, setDataSource] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [error, setError] = useState("");


  const [technicians, setTechnicians] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [requests, setRequests] = useState([]);

  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    fktechnicianId: "",
    fkCustomerId: "",
    fkstatusId: "",
    fkrequestId: "",
  });

 
  useEffect(() => {
    Promise.all([
      axios.get(`${URL}/technicians`),
      axios.get(`${URL}/customers`),
      axios.get(`${URL}/statuses`),
      axios.get(`${URL}/requests`),
    ])
      .then(([t, c, s, r]) => {
        setTechnicians(t.data || []);
        setCustomers(c.data || []);
        setStatuses(s.data || []);
        setRequests(r.data || []);
      })
      .catch((err) => console.error("Dropdown error", err));
  }, []);

  const fetchReport = async () => {
    const hasAnyFilter = Object.values(filters).some((v) => v);

    if (!hasAnyFilter) {
      setError("Please select at least one filter");
      setShowTable(false);
      return;
    }

    setError("");
    setLoading(true);

    try {
      const params = {
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        fktechnicianId: filters.fktechnicianId || undefined,
        fkCustomerId: filters.fkCustomerId || undefined,
        fkstatusId: filters.fkstatusId || undefined,
        fkrequestId: filters.fkrequestId || undefined,
      };

      const res = await axios.get(`${URL}/installations/report`, { params });
      setDataSource(res.data.data || []);
      setShowTable(true);
    } catch (err) {
      console.error("Report fetch error", err);
    }

    setLoading(false);
  };

  const columns = useMemo(
    () => [
      { accessorKey: "technicianName", header: "Technician" },
      { accessorKey: "customerName", header: "Customer" },
      { accessorKey: "statusName", header: "Status" },
      { accessorKey: "requestName", header: "Request" },
      { accessorKey: "MobileNo", header: "Mobile" },
      { accessorKey: "price", header: "Price" },
      {
        accessorKey: "createdAt",
        header: "Date",
        Cell: ({ cell }) =>
          cell.getValue()
            ? dayjs(cell.getValue()).format("DD-MM-YYYY")
            : "-",
      },
    ],
    []
  );

 const fieldStyle = {
    "& .MuiOutlinedInput-root": {
      minHeight: "28px",
      fontSize: "14px",
      "& fieldset": { borderColor: "#c4c4c4" },
      "&:hover fieldset": { borderColor: "#184459ff" },
      "&.Mui-focused fieldset": {
        borderColor: "#c4c4c4",
        borderWidth: "1.5px",
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: "18px",
      color: "#184459ff",
      fontWeight: 600,
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "#184459ff" },
  };

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
    
      <Box
        sx={{
          fontWeight: 700,
          fontSize: "22px",
          color: "#174253",
          borderLeft: "5px solid #174253",
          pl: 2,
          mb: 2,
        }}
      >
         Report
      </Box>

    
      <Box sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 2,
          }}
        >
          <TextField
            label="Start Date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters.startDate}
            onChange={(e) =>
              setFilters({ ...filters, startDate: e.target.value })
            }
            sx={fieldStyle}
          />

          <TextField
            label="End Date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters.endDate}
            onChange={(e) =>
              setFilters({ ...filters, endDate: e.target.value })
            }
            sx={fieldStyle}
          />

          <TextField
            select
            label="Technician"
            size="small"
            value={filters.fktechnicianId}
            onChange={(e) =>
              setFilters({ ...filters, fktechnicianId: e.target.value })
            }
           sx={fieldStyle}
                InputLabelProps={{ shrink: true }}
          >
            {technicians.map((t) => (
              <MenuItem key={t.technicianId} value={t.technicianId}>
                {t.technicianName}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Customer"
            size="small"
            value={filters.fkCustomerId}
            onChange={(e) =>
              setFilters({ ...filters, fkCustomerId: e.target.value })
            }
            sx={fieldStyle}
            InputLabelProps={{ shrink: true }}
          >
            {customers.map((c) => (
              <MenuItem key={c.CustomerId} value={c.CustomerId}>
                {c.CustomerName}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Status"
            size="small"
            value={filters.fkstatusId}
            onChange={(e) =>
              setFilters({ ...filters, fkstatusId: e.target.value })
            }
            sx={fieldStyle}
            InputLabelProps={{ shrink: true }}
          >
            {statuses.map((s) => (
              <MenuItem key={s.statusId} value={s.statusId}>
                {s.statusName}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Request"
            size="small"
            value={filters.fkrequestId}
            onChange={(e) =>
              setFilters({ ...filters, fkrequestId: e.target.value })
            }
            sx={fieldStyle}
            InputLabelProps={{ shrink: true }}
          >
            {requests.map((r) => (
              <MenuItem key={r.requestId} value={r.requestId}>
                {r.requestName}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {error && (
          <Box sx={{ color: "red", mt: 1, fontSize: "14px" }}>
            {error}
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button
            variant="contained"
            sx={{ bgcolor: "#174253" }}
            onClick={fetchReport}
          >
            Search
          </Button>
        </Box>
      </Box>

      {showTable && (
          <Box sx={{ background: "#fff", borderRadius: "0 0 15px 15px" }}>
                 <MaterialReactTable
                   columns={columns}
                   data={dataSource}
                   enableColumnActions={false}
                   enableDensityToggle={false}
                   enableFullScreenToggle={false}
                   enableColumnFilters={false}
                   enableHiding={false}
                   enableGlobalFilter={false}
                   muiTablePaperProps={{ sx: { boxShadow: "none", background: "#fff", borderRadius: "15px" } }}
                   muiTableContainerProps={{ sx: { backgroundColor: "#fff", width: "100%", overflowX: "auto", borderRadius: "12px" } }}
                   muiTableHeadCellProps={{ sx: { backgroundColor: "#e0e0e0ff", color: "#393e4f", fontWeight: 600, fontSize: "16px", borderBottom: "0.5px solid #e3e5eb", textAlign: "left", height: "63px", paddingTop: "23px" } }}
                   muiTableBodyRowProps={{ sx: { backgroundColor: "#fff", "&:hover": { backgroundColor: "#eff6fe" }, "& td": { fontSize: { xs: "12px", sm: "14px" }, p: "8px", borderBottom: "0.5px solid #e3e5eb", height: "54px" } } }}
                   initialState={{ pagination: { pageIndex: 0, pageSize: 10 } }}
                 />
               </Box>   )}
    </Box>
  );
};

export default InstallationReport;
